<template>
  <div class="api-credits-wallet max-w-3xl mx-auto space-y-5 p-4 md:p-6">

    <!-- Header: Título + Balance -->
    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div>
        <h2 class="text-xl font-semibold text-white">Benchmark credits</h2>
        <p class="text-sm text-white/50 mt-1">
          Prepaid balance for running benchmarks. Separate from marketplace credits.
        </p>
      </div>
      <div class="shrink-0 rounded-2xl border border-echoes-border-gray bg-echoes-background-gray/40 px-5 py-4 min-w-[180px]">
        <p class="text-xs uppercase tracking-widest text-white/40 mb-1">Balance</p>
        <p class="text-2xl font-bold text-white leading-none">
          {{ isLoading ? '—' : formatCredits(balance?.balance_credits) }}
        </p>
        <p class="text-xs text-white/40 mt-1">≈ ${{ isLoading ? '—' : formatUsd(balance?.balance_credits) }}</p>
      </div>
    </div>

    <!-- Alerta de saldo baixo -->
    <div
      v-if="balance?.low_balance"
      class="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3"
    >
      <i class="uil uil-exclamation-triangle text-amber-400 text-lg mt-0.5 shrink-0"></i>
      <p class="text-sm text-amber-100">
        Your Benchmark credit balance is low. Buy more credits before running benchmarks.
      </p>
    </div>

    <!-- Comprar créditos -->
    <div class="rounded-2xl border border-echoes-border-gray overflow-hidden">
      <div class="px-5 py-4 border-b border-echoes-border-gray">
        <h3 class="text-white font-semibold">Buy credits</h3>
        <p class="text-xs text-white/40 mt-0.5">Select a package to top up your balance</p>
      </div>

      <div class="p-5">
        <BuyBenchmarkCreditsPanel
          element-id="wallet-credits-card-element"
          @payment-success="handlePaymentSuccess"
        />
      </div>
    </div>

    <!-- Histórico de transações -->
    <div class="rounded-2xl border border-echoes-border-gray overflow-hidden">
      <div class="px-5 py-4 border-b border-echoes-border-gray flex items-center justify-between">
        <h3 class="text-white font-semibold">Transaction history</h3>
        <button
          type="button"
          class="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors disabled:opacity-30"
          :disabled="isLoading || isLedgerLoading"
          @click="refresh"
        >
          <i class="uil uil-redo text-sm"></i>
          Refresh
        </button>
      </div>

      <div v-if="isLoading || isLedgerLoading" class="p-6 flex items-center gap-2.5 text-white/40 text-sm">
        <svg class="size-4 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading transactions…
      </div>

      <div v-else-if="!ledgerItems.length" class="p-8 text-center">
        <i class="uil uil-receipt text-white/15 text-4xl block mb-2"></i>
        <p class="text-sm text-white/40">No transactions yet</p>
      </div>

      <div v-else>
        <div class="divide-y divide-echoes-border-gray">
          <div
            v-for="entry in ledgerItems"
            :key="entry.id"
            class="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
          >
            <div class="flex items-center gap-3">
              <div
                class="size-8 rounded-full flex items-center justify-center shrink-0"
                :class="
                  Number(entry.amount_micro) >= 0
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : 'bg-red-500/15 text-red-400'
                "
              >
                <i
                  class="text-sm"
                  :class="
                    Number(entry.amount_micro) >= 0
                      ? 'uil uil-arrow-down-left'
                      : 'uil uil-arrow-up-right'
                  "
                ></i>
              </div>
              <div>
                <p class="text-sm text-white font-medium capitalize">{{ entry.type }}</p>
                <p class="text-xs text-white/40">
                  {{ formatDate(entry.created_at)
                  }}<span v-if="entry.reference_type"> · {{ entry.reference_type }}</span>
                </p>
              </div>
            </div>
            <div class="text-right pl-11 sm:pl-0">
              <p
                class="text-sm font-semibold"
                :class="Number(entry.amount_micro) >= 0 ? 'text-emerald-400' : 'text-red-400'"
              >
                {{ Number(entry.amount_micro) >= 0 ? '+' : '' }}{{ formatCredits(entry.amount_credits) }}
              </p>
              <p class="text-xs text-white/40">
                Bal. {{ formatCredits(entry.balance_after_credits) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Paginação -->
        <div
          v-if="hasLedgerPrevPage || hasLedgerNextPage"
          class="px-5 py-3 border-t border-echoes-border-gray flex items-center justify-between"
        >
          <span class="text-xs text-white/40">Page {{ ledgerPage }}</span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="!hasLedgerPrevPage || isLedgerLoading"
              @click="goToLedgerPage(ledgerPage - 1)"
            >
              <i class="uil uil-angle-left text-base"></i>
              Prev
            </button>
            <button
              type="button"
              class="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="!hasLedgerNextPage || isLedgerLoading"
              @click="goToLedgerPage(ledgerPage + 1)"
            >
              Next
              <i class="uil uil-angle-right text-base"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast';
import BuyBenchmarkCreditsPanel from '@/components/modals/BuyBenchmarkCreditsPanel.vue';
import {
  getApiCreditsBalance,
  getApiCreditsLedger,
} from '@/services/apiCreditsService.js';

const { addToast } = useToast();
const route = useRoute();
const router = useRouter();

const LEDGER_PAGE_SIZE = 10;

const isLoading = ref(false);
const isLedgerLoading = ref(false);
const balance = ref(null);
const ledgerItems = ref([]);
const ledgerPage = ref(1);
const ledgerTotal = ref(0);

const hasLedgerPrevPage = computed(() => ledgerPage.value > 1);

// Se a API retornar `total`, calcula com base nele; caso contrário usa heurística
// (recebemos exatamente PAGE_SIZE itens → pode haver mais)
const hasLedgerNextPage = computed(() => {
  if (ledgerTotal.value > 0) {
    return ledgerPage.value * LEDGER_PAGE_SIZE < ledgerTotal.value;
  }
  return ledgerItems.value.length === LEDGER_PAGE_SIZE;
});

const formatCredits = (value) => {
  const num = Number(value ?? 0);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
};

const formatUsd = (value) => {
  const num = Number(value ?? 0);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const fetchLedger = async (page = 1) => {
  isLedgerLoading.value = true;
  try {
    const ledgerData = await getApiCreditsLedger({
      skip: (page - 1) * LEDGER_PAGE_SIZE,
      take: LEDGER_PAGE_SIZE,
    });
    ledgerItems.value = ledgerData?.items ?? [];
    if (typeof ledgerData?.total === 'number') {
      ledgerTotal.value = ledgerData.total;
    } else {
      ledgerTotal.value = 0;
    }
    ledgerPage.value = page;
  } catch (error) {
    addToast(error.response?.data?.message || 'Unable to load transactions', 'error');
  } finally {
    isLedgerLoading.value = false;
  }
};

const goToLedgerPage = (page) => {
  if (page < 1 || isLedgerLoading.value) return;
  fetchLedger(page);
};

const refresh = async () => {
  isLoading.value = true;
  try {
    balance.value = await getApiCreditsBalance();
  } catch (error) {
    addToast(error.response?.data?.message || 'Unable to load Benchmark credits wallet', 'error');
  } finally {
    isLoading.value = false;
  }
  await fetchLedger(1);
};

const handlePaymentSuccess = async (payload) => {
  if (payload?.balance) {
    balance.value = payload.balance;
  }
  await refresh();
};

onMounted(async () => {
  await refresh();
  if (route.query.topup === '1' || route.query.payment_intent) {
    addToast('Checking your Benchmark credit top-up...', 'success');
    await refresh();
    const nextQuery = { ...route.query, page: 'api-credits' };
    delete nextQuery.topup;
    delete nextQuery.payment_intent;
    delete nextQuery.payment_intent_client_secret;
    delete nextQuery.redirect_status;
    router.replace({ query: nextQuery });
  }
});
</script>
